import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loggedIn } from '../../../actions/user';
import { confirmNewPassword, sendConfirmCode } from '../../../actions/user-account';

import {
    FormControl, InputLabel, OutlinedInput, InputAdornment,
    IconButton, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button, Stack, TextField, FormHelperText
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';

import { regex_num } from '../../../constants/regex';
import { validateNewPassword } from '../../../helpers/security';

const INIT = { pwd1: '', pwd2: '', code: '' };
const INIT2 = {
    show1: false, // mostrar contraseña
    show2: false, // mostrar contraseña (campo 2)
    loading1: false,
    loading2: false,
    pwd_err: false, // contraseñas no coinciden
    code_err: false,
    code_field: false // actualizar/confirmar contraseña (mostrar campo)
};

const NewPasswordDialog = ({ dialog2, setFieldStatus }) => {
    const dispatch = useDispatch();
    // usuario iniciado
    const { loggedUser } = useSelector(state => state.user);
    const { email } = loggedUser;

    // referencias a campos
    const cInputRef = useRef();

    const [data, setData] = useState(INIT);
    const { pwd1, pwd2, code } = data;
    const passwordError = validateNewPassword(pwd1, loggedUser);

    const [interfaceData, setInterfaceData] = useState(INIT2);
    const {
        show1, show2, loading1, loading2,
        pwd_err, code_err, code_field
    } = interfaceData;

    useEffect(() => {
        setInterfaceData(e => ({
            ...e, pwd_err: pwd1 !== pwd2 ? true : false
        }));
    }, [pwd1, pwd2]);

    const handleOnChange = ({ target }) => {
        const name = target.name;
        const value = target.value;
        if (['pwd1', 'pwd2'].includes(name)) {
            if (value === '' || value.length <= 64)
                setData(e => ({ ...e, [name]: value }));
        } else if (name === 'code' && code_field) {
            if (value === '' || (value.length <= 6 && regex_num.test(value)))
                setData(e => ({ ...e, code: value }));
            setInterfaceData(e => ({
                ...e, code_err: value.length !== 6 ? true : false
            }));
        }
    };
    
    // solicitar código para confirmar contraseña
    const requestCode = async () => {
        if (!passwordError && pwd1 === pwd2) {
            setInterfaceData(e => ({
                ...e, loading1: true
            }));
            const req = await sendConfirmCode(email);
            if (req.status) {
                setInterfaceData(e => ({
                    ...e, code_field: true, loading1: false
                }));
            } else
                setInterfaceData(e => ({
                    ...e, loading1: false
                }));
        }
    };

    // confirmar nueva contraseña
    const confirmPassword = async () => {
        if (code.length === 6) {
            setInterfaceData(e => ({ ...e, loading2: true }));
            const req = await confirmNewPassword({
                code, email, password: pwd1, flag: true
            });
            if (req.status) {
                // usuario ahora cuenta con contraseña
                // actualizar estado de aplicación
                dispatch(loggedIn({ no_pwd: false }));
                setInterfaceData(e => ({
                    ...e, ...INIT2
                }));
                setFieldStatus(e => ({ ...e, dialog2: false }));
                setData(e => ({ ...e, ...INIT }));
            } else
                setInterfaceData(e => ({
                    ...e, loading2: false
                }));
        }
    };

    return (
        <Dialog open={ dialog2 }
            onClose={ () => setFieldStatus(e => ({ ...e, dialog2: false })) }
            sx={{ zIndex: 1000 }}>
            <DialogTitle>Nueva contraseña</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Necesita establecer una contraseña para inicio de sesión y futuros cambios en su cuenta.
                </DialogContentText>

                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Contraseña</InputLabel>
                    <OutlinedInput
                        type={ show1 ? 'text' : 'password' }
                        style={{ backgroundColor: '#fff' }}
                        name='pwd1'
                        value={ pwd1 }
                        disabled={ code_field }
                        inputProps={{ maxLength: 64 }}
                        onChange={ handleOnChange }
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={ () => setInterfaceData(e => ({ ...e, show1: !e.show1 })) }
                                    edge="end"
                                >
                                    {
                                        show1 ?
                                            <i className="far fa-eye-slash" style={{ fontSize: '1.3rem' }}></i>
                                        :
                                            <i className="far fa-eye" style={{ fontSize: '1.3rem' }}></i>
                                    }
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Contraseña"
                    />
                    {pwd1 && passwordError &&
                        <FormHelperText error>{passwordError}</FormHelperText>}
                </FormControl>
                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Confirme contraseña</InputLabel>
                    <OutlinedInput
                        type={ show2 ? 'text' : 'password' }
                        style={{ backgroundColor: '#fff' }}
                        name='pwd2'
                        value={ pwd2 }
                        disabled={ code_field }
                        inputProps={{ maxLength: 64 }}
                        onChange={ handleOnChange }
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={ () => setInterfaceData(e => ({ ...e, show2: !e.show2 })) }
                                    edge="end"
                                >
                                    {
                                        show2 ?
                                            <i className="far fa-eye-slash" style={{ fontSize: '1.3rem' }}></i>
                                        :
                                            <i className="far fa-eye" style={{ fontSize: '1.3rem' }}></i>
                                    }
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Confirme contraseña"
                    />
                    {
                        pwd_err &&
                            <FormHelperText error>
                                LAS CONTRASEÑAS NO COINCIDEN
                            </FormHelperText>
                    }
                </FormControl>
                <Stack spacing={2} sx={{ mt: 2 }}
                    alignItems='flex-start'>
                    {
                        code_field &&
                            <TextField
                                label="Código de acceso"
                                type="code"
                                variant="standard"
                                error={ code_err }
                                helperText={ code_err ? "6 dígitos" : "" }
                                inputRef={ cInputRef }
                                inputProps={{ maxLength: 6 }}
                                name="code"
                                value={ code }
                                onChange={ handleOnChange }
                                disabled={ loading2 }
                            />
                    }
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='text'
                    onClick={ () => setFieldStatus(e => ({ ...e, dialog2: false })) }>
                    Cancelar
                </Button>
                {
                    code_field ?
                        loading2 ?
                            <LoadingButton
                                loading
                                loadingPosition="start"
                                startIcon={<SaveIcon />}
                                variant="outlined">
                                Confirmar
                            </LoadingButton>
                        :
                            <Button variant='contained'
                                onClick={ confirmPassword }>
                                Confirmar
                            </Button>
                    :
                        loading1 ?
                            <LoadingButton
                                loading
                                loadingPosition="start"
                                startIcon={<SaveIcon />}
                                variant="outlined">
                                Actualizar
                            </LoadingButton>
                        :
                            <Button variant='outlined'
                                onClick={ requestCode }>
                                Actualizar
                            </Button>
                }
            </DialogActions>
        </Dialog>
    );
};

export default NewPasswordDialog;
